import { ReactNode, useCallback } from "react";
import { pluralize } from "@common/helpers/string";
import { numberFormatter } from "@common/helpers/number";
import { Pagination } from "@design-system/Pagination/Pagination";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { BulkSelectionControl } from "@design-system/BulkSelectionControl/BulkSelectionControl";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { HStack } from "@design-system/HStack/HStack";
import { useDocumentBulkActions } from "../useDocumentBulkActions";

export interface FinancialDocumentsViewFooterPagination {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

interface FinancialDocumentsViewFooterProps {
    total: number;
    /** More rows match than the table holds, so "Select all" can show. */
    hasUnloadedRows: boolean;
    /** Absent when the table loads more on scroll. */
    pagination?: FinancialDocumentsViewFooterPagination;
    selectionActions?: ReactNode;
}

export const FinancialDocumentsViewFooter = ({
    total,
    hasUnloadedRows,
    pagination,
    selectionActions,
}: FinancialDocumentsViewFooterProps) => {
    const {
        bulkSelection,
        bulkActions,
        isRemoveConfirmationOpen,
        setIsRemoveConfirmationOpen,
        isUnmatchConfirmationOpen,
        setIsUnmatchConfirmationOpen,
        isAllReconciled,
        removableCount,
        matchedDocumentsCount,
        reconciledCount,
        confirmRemoveButtonLabel,
        transactionsToRemoveCount,
        setTransactionsToRemoveCount,
        handleRemove,
        handleConfirmTransactionsRemoval,
        handleUnmatch,
        handleOpenBulkActions,
        showSelectAll,
    } = useDocumentBulkActions({ hasUnloadedRows });

    const onPageChange = pagination?.onPageChange;
    const pageChangeWithBulkActionReset = useCallback(
        (newPage: number) => {
            if (
                bulkSelection.selectedCount &&
                !bulkSelection.hasSelectedByCurrentFilters
            ) {
                bulkSelection.clearSelection();
            }
            onPageChange?.(newPage);
        },
        [bulkSelection, onPageChange],
    );

    if (!total) {
        return null;
    }

    const hasSelection = bulkSelection.selectedCount > 0;

    return (
        <>
            <CommandIsland
                sticky
                className={commandIslandStyles.bulkActionsStickyFooter}
            >
                <HStack gap={8}>
                    {pagination && pagination.totalPages > 1 ? (
                        <Pagination
                            pageCount={pagination.totalPages}
                            currentPage={pagination.currentPage}
                            onPageChange={pageChangeWithBulkActionReset}
                            size="md"
                            buttonsPosition="left"
                        />
                    ) : (
                        <CommandIslandTotal>
                            {total} {pluralize("document", total)}
                        </CommandIslandTotal>
                    )}

                    {hasSelection && (
                        <>
                            <BulkSelectionControl
                                selectedCount={bulkSelection.selectedCount}
                                onClearSelection={bulkSelection.clearSelection}
                                size="md"
                            />
                            {showSelectAll && (
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={bulkSelection.selectAll}
                                    data-testid="bulk-select-all-button"
                                >
                                    Select all{" "}
                                    {numberFormatter.format(
                                        bulkSelection.totalCount,
                                    )}
                                </Button>
                            )}
                        </>
                    )}
                </HStack>

                {hasSelection && (
                    <HStack gap={8}>
                        {selectionActions}
                        <Tooltip content="Open bulk actions">
                            <Button
                                variant="primary"
                                size="md"
                                onClick={handleOpenBulkActions}
                                disabled={bulkActions.busy}
                                rightIcon={
                                    <span data-slot-box="">
                                        <Shortcut keys="mod+k" />
                                    </span>
                                }
                            >
                                Actions
                            </Button>
                        </Tooltip>
                    </HStack>
                )}
            </CommandIsland>

            <ConfirmationModal
                show={isRemoveConfirmationOpen}
                onHide={() => setIsRemoveConfirmationOpen(false)}
                closeButton
                title="Remove selected documents"
                question={
                    isAllReconciled ? (
                        <>
                            All selected documents are used in bank
                            reconciliations and cannot be removed. Undo the
                            reconciliation first.
                        </>
                    ) : (
                        <>
                            You're about to remove {removableCount}{" "}
                            {pluralize("document", removableCount)}.
                            {reconciledCount > 0 && (
                                <>
                                    {" "}
                                    Some documents are used in bank
                                    reconciliations and will be skipped.
                                </>
                            )}{" "}
                            This action cannot be undone. Do you want to
                            continue?
                        </>
                    )
                }
                onConfirm={
                    isAllReconciled
                        ? () => setIsRemoveConfirmationOpen(false)
                        : handleRemove
                }
                onReject={
                    isAllReconciled
                        ? undefined
                        : () => setIsRemoveConfirmationOpen(false)
                }
                yes={confirmRemoveButtonLabel}
                no="Cancel"
            />

            <ConfirmationModal
                show={isUnmatchConfirmationOpen}
                onHide={() => setIsUnmatchConfirmationOpen(false)}
                closeButton
                title="Unmatch selected documents"
                question={
                    <>
                        You're about to unmatch {matchedDocumentsCount}{" "}
                        {pluralize("document", matchedDocumentsCount)} from
                        their linked transactions. This action cannot be undone.
                        Do you want to continue?
                    </>
                }
                onConfirm={handleUnmatch}
                onReject={() => setIsUnmatchConfirmationOpen(false)}
                yes="Unmatch documents"
                no="Cancel"
            />

            <ConfirmationModal
                show={Boolean(transactionsToRemoveCount)}
                onHide={() => setTransactionsToRemoveCount(undefined)}
                closeButton
                title={`Are you sure you want to remove ${transactionsToRemoveCount} imported ${pluralize("transaction", transactionsToRemoveCount ?? 0)}?`}
                question={
                    <span className="text-danger">
                        Warning: This action cannot be undone.
                    </span>
                }
                onConfirm={handleConfirmTransactionsRemoval}
                onReject={() => setTransactionsToRemoveCount(undefined)}
                yes="Remove"
                yesBtnVariant="destructive"
                no="Cancel"
            />
        </>
    );
};
